import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Check, ArrowRight, Star, ChevronDown } from "lucide-react";

const PLANS = [
  {
    id: "standard",
    name: "Website Build",
    price: "$900",
    unit: "one-time",
    shortDesc: "A fully custom website designed for your business, launched live on the internet.",
    heading: "Website Build — $900 one-time",
    longDesc: "A fully custom website designed for your business, launched live on the internet.",
    included: [
      "Custom design tailored to your business",
      "Mobile responsive",
      "Live within 72 hours",
      "Built to convert visitors into customers",
    ],
    sections: [
      {
        title: "Domain",
        node: "Not included. You'll register and manage your own domain (typically $10–$20/year depending on the name). We'll connect it to your site once you have it.",
      },
      {
        title: "Ongoing changes",
        node: "Need updates later? One-off edits are available for $50 per request, or get 3 edits included every month by upgrading to our Build + Maintenance plan.",
      },
    ],
    recommended: false,
  },
  {
    id: "build_maintenance",
    name: "Build + Maintenance",
    price: "$600",
    unit: "one-time + $79.99/month",
    shortDesc: "Everything you need to launch and grow — we handle your domain and keep your site running smoothly every month.",
    heading: "Build + Maintenance — $600 one-time + $79.99/month",
    longDesc: "Everything in Website Build, plus ongoing care so you never have to think about the technical side.",
    included: [
      "Everything in Website Build (custom design, mobile responsive, live within 72 hours, built to convert)",
      "Domain registered and managed for you (billed to you at cost, no markup — we just handle the setup)",
      "3 edits included every month - structural changes and minor design tweaks (major redesigns and full page rebuilds not included). Need more in a month? Just $25 per additional edit.",
      "Priority support",
      "We handle the technical stuff so you don't have to",
    ],
    sections: [
      {
        title: "Monthly billing terms",
        node: "Billed monthly for a 12-month term. Cancel early and a $300 fee applies to bring your build cost up to the standalone $900 price.",
      },
      {
        title: "Payment processing",
        node: "All prices are subject to a small card processing fee (covers the cost charged to us by our payment processor), itemized separately at checkout. This applies to one-time payments, the monthly maintenance subscription, and any additional-edit charges.",
      },
      {
        title: "Want full independence later?",
        node: (
          <>
            If you ever want to take over your site and domain completely, a one-time transfer fee applies to cover the handoff. Email us at{" "}
            <a href="mailto:elevatedweb.tx@gmail.com" className="text-sky-brand underline underline-offset-2">elevatedweb.tx@gmail.com</a>{" "}
            to discuss details.
          </>
        ),
      },
    ],
    recommended: true,
  },
];

function PlanCard({ p, expanded, onToggle, onChoose }) {
  const dark = p.recommended;
  const subText = dark ? "text-white/70" : "text-gray-500";
  const bodyText = dark ? "text-white/80" : "text-charcoal/80";
  return (
    <div
      data-testid={`plan-card-${p.id}`}
      className={`relative rounded-[2rem] p-8 md:p-10 h-full flex flex-col transition-all duration-500 ${
        dark
          ? "bg-charcoal text-white shadow-[0_25px_60px_rgba(0,0,0,0.25)] ring-2 ring-sky-brand"
          : "bg-white border border-gray-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)]"
      }`}
    >
      {dark && (
        <span className="absolute top-8 right-8 font-display text-[0.65rem] uppercase tracking-widest font-semibold bg-sky-brand text-charcoal px-3 py-1 rounded-full inline-flex items-center gap-1">
          <Star size={12} /> Recommended
        </span>
      )}

      <h3 className={`font-display text-2xl font-semibold ${dark ? "text-white" : "text-charcoal"}`}>{p.name}</h3>
      <div className="mt-4 flex items-baseline gap-1 flex-wrap">
        <span className="font-display text-5xl font-bold text-sky-brand">{p.price}</span>
        <span className={`font-body text-sm ${dark ? "text-white/60" : "text-gray-400"}`}>{p.unit}</span>
      </div>
      <p className={`font-body mt-5 leading-relaxed ${subText}`}>{expanded ? p.longDesc : p.shortDesc}</p>

      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            key="details"
            data-testid={`plan-details-${p.id}`}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden"
          >
            <div className="pt-6">
              <h4 className={`font-display text-sm uppercase tracking-widest font-semibold text-sky-brand`}>What's included</h4>
              <ul className="mt-4 space-y-3">
                {p.included.map((f) => (
                  <li key={f} className={`flex items-start gap-3 font-body text-sm ${bodyText}`}>
                    <Check size={18} className="text-sky-brand shrink-0 mt-0.5" /> {f}
                  </li>
                ))}
              </ul>
              {p.sections.map((s) => (
                <div key={s.title} className={`mt-6 pt-5 border-t ${dark ? "border-white/10" : "border-gray-100"}`}>
                  <h4 className={`font-display font-semibold ${dark ? "text-white" : "text-charcoal"}`}>{s.title}</h4>
                  <p className={`font-body text-sm mt-2 leading-relaxed ${subText}`}>{s.node}</p>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="mt-8 flex flex-col gap-3">
        {expanded && (
          <button
            type="button"
            data-testid={`plan-choose-${p.id}`}
            onClick={onChoose}
            className={`font-display font-semibold px-6 py-3.5 rounded-full inline-flex items-center justify-center gap-2 transition-colors duration-300 ${
              dark ? "bg-sky-brand text-charcoal hover:bg-white" : "bg-charcoal text-white hover:bg-sky-brand hover:text-charcoal"
            }`}
          >
            Get Started <ArrowRight size={16} />
          </button>
        )}
        <button
          type="button"
          data-testid={`plan-details-toggle-${p.id}`}
          onClick={onToggle}
          className={`font-display font-semibold px-6 py-3.5 rounded-full inline-flex items-center justify-center gap-2 transition-colors duration-300 border ${
            dark
              ? "border-white/25 text-white hover:border-sky-brand hover:text-sky-brand"
              : "border-gray-200 text-charcoal hover:border-charcoal"
          }`}
        >
          {expanded ? "Hide Details" : "See Details"}
          <ChevronDown size={16} className={`transition-transform duration-300 ${expanded ? "rotate-180" : ""}`} />
        </button>
      </div>
    </div>
  );
}

export default function PricingCards() {
  const [expanded, setExpanded] = useState({});
  const navigate = useNavigate();

  const toggle = (id) => setExpanded((cur) => ({ ...cur, [id]: !cur[id] }));
  const choose = () => navigate("/contact");

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-start">
        {PLANS.map((p) => (
          <PlanCard
            key={p.id}
            p={p}
            expanded={!!expanded[p.id]}
            onToggle={() => toggle(p.id)}
            onChoose={choose}
          />
        ))}
      </div>
      <p
        data-testid="pricing-fee-note"
        className="font-body text-sm text-gray-400 text-center mt-6 max-w-2xl mx-auto"
      >
        Prices shown don't include card processing fees, which are itemized separately at checkout.
      </p>
    </>
  );
}
